<!-- ═══════════════════════════════════════════════════════════════════════
     FILE: eisenhower-panel.component.html · Eisenhower-Fenster Alt+N (23.10.2026)
     Knopf unten links (über „Ask“) + Fenster mit vier Feldern.
     Testids: eis.launcher · eis.badge · eis.panel · eis.close · eis.refresh · eis.loading · eis.error
              eis.q.{do_now|plan|delegate|drop} · eis.count.{quadrant} · eis.item-{quadrant}-{typ}-{id}
              eis.go-{quadrant}-{typ}-{id} · eis.delegate-{quadrant}-{typ}-{id} · eis.more-{quadrant}-{typ}-{id}
              eis.self · eis.explain-toggle · eis.explain
     ═══════════════════════════════════════════════════════════════════ -->

@if (!req.panelOpen()) {
  <button type="button" class="eis-launcher" data-testid="eis.launcher"
          [class.eis-launcher--hot]="doNowCount() > 0" (click)="toggle()"
          [attr.aria-label]="doNowCount() > 0 ? doNowCount() + ' things to do now — Alt+N' : 'What should I do now? — Alt+N'"
          title="What should I do now? — Alt+N">
    <span class="eis-launcher__label">Now</span>
    <kbd class="eis-launcher__kbd">Alt+N</kbd>
    @if (doNowCount() > 0) { <span class="eis-launcher__badge" data-testid="eis.badge">{{ doNowCount() }}</span> }
  </button>
}

@if (open()) {
  <div class="eis-backdrop" (click)="close()"></div>
  <section class="eis" data-testid="eis.panel" role="dialog" aria-modal="true" aria-labelledby="eis-title">
    <header class="eis__head">
      <div>
        <h2 id="eis-title" class="eis__title">What should I do now?</h2>
        @if (data(); as d) {
          <p class="eis__meta">{{ d.count }} elements checked · {{ d.money_path }} on the path to paying customers ·
            {{ ageMin(d.generated_at) }}</p>
        }
      </div>
      <div class="eis__actions">
        <button type="button" class="eis__btn" data-testid="eis.refresh" (click)="load(false)" [disabled]="loading()">Refresh</button>
        <button #closeBtn type="button" class="eis__x" data-testid="eis.close" (click)="close()" aria-label="Close (Esc)">×</button>
      </div>
    </header>

    @if (loading() && !data()) { <p class="eis__state" data-testid="eis.loading">Loading your priorities…</p> }
    @if (error()) { <p class="eis__state eis__state--error" data-testid="eis.error">{{ error() }}</p> }

    @if (data(); as d) {
      <div class="eis__grid">
        @for (q of quadrants; track q.key) {
          <section class="eis-q eis-q--{{ q.key }}" [attr.data-testid]="'eis.q.' + q.key">
            <header class="eis-q__head">
              <h3 class="eis-q__title">{{ q.title }}</h3>
              <span class="eis-q__count" [attr.data-testid]="'eis.count.' + q.key">{{ d.counts[q.key] }}</span>
              <p class="eis-q__sub">{{ q.sub }}</p>
            </header>
            @if (!d.quadrants[q.key].length) {
              <p class="eis-q__empty">Nothing here.</p>
            }
            <ol class="eis-q__list">
              @for (e of d.quadrants[q.key]; track key(e)) {
                <li class="eis-e" [attr.data-testid]="'eis.item-' + key(e)">
                  <div class="eis-e__line">
                    <span class="eis-e__rank" [title]="'importance ' + e.importance.score + ' · urgency ' + e.urgency.score">{{ e.rank }}</span>
                    <strong class="eis-e__verb">{{ e.verb }}</strong>
                    @if (e.money_path) { <span class="eis-tag eis-tag--money" title="On the path to paying customers">revenue</span> }
                    @if (e.waiting_people > 0) { <span class="eis-tag" title="People waiting on you">{{ e.waiting_people }} waiting</span> }
                  </div>
                  <button type="button" class="eis-e__target" [attr.data-testid]="'eis.go-' + key(e)" (click)="go(e)">
                    <span class="eis-e__pslot">{{ pslotShort(e.target.pslot) }} {{ e.target.typ }} #{{ e.target.id }}</span>
                    {{ e.target.name }}
                  </button>
                  <p class="eis-e__why">{{ e.reason }}</p>
                  @if (e.delegate) {
                    <div class="eis-e__deleg">
                      <span>→ {{ delegateName(e.delegate) }} <small>({{ e.delegate.why }})</small></span>
                      <button type="button" class="eis__btn eis__btn--small" [attr.data-testid]="'eis.delegate-' + key(e)"
                              (click)="delegate(e)">Delegate</button>
                    </div>
                  }
                  <button type="button" class="eis-e__more" [attr.data-testid]="'eis.more-' + key(e)" (click)="toggleMore(e)"
                          [attr.aria-expanded]="expanded() === key(e)">
                    {{ expanded() === key(e) ? 'Less' : 'Why this rank' }}{{ e.more.length ? ' · ' + e.more.length + ' more step(s)' : '' }}
                  </button>
                  @if (expanded() === key(e)) {
                    <div class="eis-e__detail">
                      <p>Importance {{ e.importance.score }} (value from {{ e.importance.value_source }}) ·
                         Urgency {{ e.urgency.score }}: {{ urgencyWhy(e) }}</p>
                      @for (m of e.more; track m.verb) {
                        <p class="eis-e__also">Also: <strong>{{ m.verb }}</strong> — {{ m.reason }}
                          @if (m.delegate) {
                            <button type="button" class="eis__btn eis__btn--small" (click)="delegate(e, m.delegate, m.verb, m.reason)">
                              → {{ delegateName(m.delegate) }}</button>
                          }
                        </p>
                      }
                    </div>
                  }
                </li>
              }
            </ol>
            @if (d.counts[q.key] > d.quadrants[q.key].length) {
              <p class="eis-q__rest">+ {{ d.counts[q.key] - d.quadrants[q.key].length }} more with a lower rank</p>
            }
          </section>
        }
      </div>

      <footer class="eis__foot">
        <p class="eis__self" data-testid="eis.self">
          @if (lastRun(); as r) {
            Since your last look ({{ r.since_min }} min): {{ r.do_now.moved + r.do_now.done }} of {{ r.do_now.n }} “Do now” moved or done
            @if (r.do_now.hit_rate !== null) { ({{ r.do_now.hit_rate }} %) }
            @if (d.self_check.do_now_hit_rate_avg !== null) { · average {{ d.self_check.do_now_hit_rate_avg }} % }
          } @else {
            Self-check starts with your next look in {{ d.self_check.next_evaluation_after_min }}+ minutes.
          }
        </p>
        <button type="button" class="eis-e__more" data-testid="eis.explain-toggle" (click)="showExplain.set(!showExplain())"
                [attr.aria-expanded]="showExplain()">How is this calculated?</button>
        @if (showExplain()) {
          <dl class="eis__explain" data-testid="eis.explain">
            <dt>Directive</dt><dd>{{ d.explain.directive }}</dd>
            <dt>Money path</dt><dd>{{ d.explain.money_path }}</dd>
            <dt>Importance</dt><dd>{{ d.explain.importance }}</dd>
            <dt>Urgency</dt><dd>{{ d.explain.urgency }}</dd>
            @for (q of quadrants; track q.key) { <dt>{{ q.title }}</dt><dd>{{ d.explain.quadrants[q.key] }}</dd> }
            <dt>What is checked</dt><dd>{{ d.explain.selection }}</dd>
            <dt>Version</dt><dd>{{ d.version }} · {{ d.read_ms }} ms</dd>
          </dl>
        }
      </footer>
    }
  </section>
}
